import React from 'react'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state/types'
import {
  CREATE_PROJECT_MODAL,
  ONE_AUTH_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { publicUrl } from '@chegg-wt/core/utils/url'
import { CITATIONS } from '@chegg-wt/core/constants/nav'
import {
  userClick,
  signinStart,
  signupStart,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  signinName,
  signinRegion,
  linkType,
  SigninStartPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamSignInStart'

import { NEW_CITATION_LIST_BUTTON } from '@chegg-wt/core/constants/citations'
import { ChevronIcon, MenuIcon, ProfileIcon } from '@chegg-ui/theme'
import { Config } from '@chegg-wt/core/config'
import { NavItem, NavItems } from '@chegg-wt/core/types/core'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import {
  topNavRegion,
  SignupStartPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamSignupStart'
import {
  currentStyleSelector,
  currentProjectIdSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import {
  userEmailSelector,
  isProSelector,
  loggedInSelector,
  isGuestSelector,
} from '@chegg-wt/core/state/user/selectors'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import { useRouter } from 'next/router'
import { useRoutesContext } from '../RoutesContext'
import AppLink from '../AppLink'
import { HiddenLabel } from '../HiddenLabel'
import PopoverMenu from '../PopoverMenu'
import { AUTH_MODAL_TITLE, AuthModalOrigin } from '../Modals/AuthModal/types'
import * as Styled from './styled'
import { HeaderNav } from './HeaderNav'
import { MAIN_MENU, MAIN_MENU_BUTTON, ACCOUNT_MENU_BUTTON } from './utils/dataTestIds'

const isElementVisible = (elementId: string) => {
  const elem = document.querySelector(`#${elementId}`)
  const style = window.getComputedStyle(elem)
  return style?.display !== 'none'
}

interface Props {
  email: string
  paidUser: boolean
  projectId: string
  style: string
  navItems: NavItems
  isLoggedIn: boolean
  authModalExpFlag: boolean
  isGuestUser: boolean
  navItemsSelector: (state: AppState) => NavItems
  signupStart: (payload: SignupStartPayload) => void
  signinStart: (payload?: SigninStartPayload) => void
  userClick: (payload: UserClickPayload) => void
  openModal: (payload: OpenModalPayload) => void
}

export function getEBPremiumCitationsHeaderItems(
  hasPremiumCitations: boolean,
  items: NavItem[],
  isPro: boolean,
  projectId: string
): NavItem[] | null {
  if (!isPro || !hasPremiumCitations || !items) {
    return null
  }
  return items.map((h) => {
    if (h.name === CITATIONS) {
      return {
        name: CITATIONS,
        hideOnSmall: true,
        route: projectId ? 'toCitationList' : 'toFolders',
        query: projectId ? { projectId } : undefined,
      }
    }
    return h
  })
}

/** header for the app with nav, menus, and user info */
export const Nav: React.FC<Props> = ({
  email,
  paidUser,
  projectId,
  style,
  navItems,
  signupStart,
  signinStart,
  userClick,
  authModalExpFlag,
  openModal,
  isGuestUser,
}) => {
  const routes = useRoutesContext()
  const router = useRouter()
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false)
  const [isMainMenuOpen, setIsMainMenuOpen] = React.useState(false)

  const { hasPremiumCitations, logoAlt, logoWidth, appName } = Config
  const ebPremiumCitationsHeaderItems = getEBPremiumCitationsHeaderItems(
    hasPremiumCitations,
    navItems.header,
    paidUser,
    projectId
  )

  const mapItems = (items, view) => items.map(view)

  const routeParams = { projectId, style }

  const handleNavClick = (navName) => {
    if (navItems.header && navItems.header.some((header) => header.name === navName)) {
      userClick({
        origin: origins.topNavItemClick,
        value: navName.toLowerCase(),
        text: navName.toLowerCase(),
      })
    } else {
      userClick({
        origin: origins.topSideNavItemClick,
        value: navName.toLowerCase(),
        text: navName.toLowerCase(),
      })
    }
    if (navName === NEW_CITATION_LIST_BUTTON) {
      openModal({ name: CREATE_PROJECT_MODAL, data: {} })
    }

    if (navName === 'Upgrade' && authModalExpFlag && isGuestUser) {
      openModal({
        name: ONE_AUTH_MODAL,
        data: {
          defaultFlow: 'signup',
          origin: AuthModalOrigin.NAV_HEADER,
          title: AUTH_MODAL_TITLE,
          redirectTo: routes.toUpgrade(),
        },
      })
    }

    setIsAccountMenuOpen(false)
  }

  /** click handler for right nav part links. i.e upgrade, sign up, sign in */
  const handleNavLinkClick = (navName: string) => {
    if (navName === 'Sign up') {
      signupStart({ region: topNavRegion })
      if (authModalExpFlag) {
        openModal({
          name: ONE_AUTH_MODAL,
          data: {
            defaultFlow: 'signup',
            origin: AuthModalOrigin.NAV_HEADER,
            redirectTo: router.pathname === '/logout' ? routes.toFolders() : undefined,
          },
        })
      }
    } else if (navName === 'Upgrade') {
      userClick({ origin: origins.upgradeTopNavClick })
      if (authModalExpFlag && isGuestUser) {
        openModal({
          name: ONE_AUTH_MODAL,
          data: {
            defaultFlow: 'signup',
            origin: AuthModalOrigin.NAV_HEADER,
            title: AUTH_MODAL_TITLE,
            redirectTo: routes.toUpgrade(),
          },
        })
      }
    } else if (navName === 'Sign in') {
      signinStart({ name: signinName, type: linkType, region_name: signinRegion })
      if (authModalExpFlag) {
        openModal({
          name: ONE_AUTH_MODAL,
          data: {
            defaultFlow: 'signin',
            origin: AuthModalOrigin.NAV_HEADER,
            redirectTo: router.pathname === '/logout' ? routes.toFolders() : undefined,
          },
        })
      }
    }
  }

  const handleHeaderNavClick = (navName) => {
    userClick({
      origin: origins.topNavItemClick,
      value: navName.toLowerCase(),
      text: navName.toLowerCase(),
    })
  }

  const handleSiteLogoClick = () => {
    userClick({
      origin: origins.siteLogoClick,
      name: `${appName} homepage shortcut`,
    })
  }

  const NavLink: React.FC<NavItem> = (item) => {
    if (
      ['Sign up', 'Sign in', 'Upgrade'].includes(item.name) &&
      authModalExpFlag &&
      isGuestUser
    ) {
      return (
        <Styled.NavLink
          hideOnMobile={item.hideOnMobile}
          onClick={() => handleNavLinkClick(item.name)}
          data-test={item.name}
        >
          {item.name}
        </Styled.NavLink>
      )
    }
    return (
      <AppLink
        route={item.route ? routes[item.route](routeParams) : { href: item.href }}
        key={item.href || item.name}
      >
        <Styled.NavLink
          hideOnMobile={item.hideOnMobile}
          onClick={() => handleNavLinkClick(item.name)}
          data-test={item.name}
        >
          {item.name}
        </Styled.NavLink>
      </AppLink>
    )
  }

  const DropdownItem: React.FC<NavItem> = (item) => {
    const params = {
      ...routeParams,
      ...item.query,
    }

    if (item.name === 'Upgrade' && authModalExpFlag && isGuestUser) {
      return (
        <Styled.DropdownItem
          key={item.href || item.name}
          onClick={() => handleNavClick(item.name)}
          hideOnSmall={item.hideOnSmall}
        >
          <Styled.DropdownLink
            className={item.icon}
            data-test={item.name}
            target={item.target}
          >
            {item.name}
          </Styled.DropdownLink>
        </Styled.DropdownItem>
      )
    }

    return (
      <Styled.DropdownItem
        key={item.href || item.name}
        onClick={() => handleNavClick(item.name)}
        hideOnSmall={item.hideOnSmall}
      >
        <AppLink
          replace={item.name === 'Logout'}
          route={item.route ? routes[item.route](params) : { href: item.href }}
        >
          <Styled.DropdownLink
            className={item.icon}
            data-test={item.name}
            target={item.target}
          >
            {item.name}
          </Styled.DropdownLink>
        </AppLink>
      </Styled.DropdownItem>
    )
  }

  const dropdownItems = (navItems.header || []).concat(navItems.main)

  const ACCOUNT_MENU_ID = 'accountMenu'
  const MAIN_MENU_ID = 'mainMenu'

  return (
    <Styled.Header>
      <PopoverMenu
        targetElement={
          <Styled.Nav
            role="navigation"
            aria-label="main menu"
            onMouseOver={() => setIsMainMenuOpen(true)}
            onMouseOut={() => setIsMainMenuOpen(isElementVisible(MAIN_MENU_ID))}
            onFocus={() => setIsMainMenuOpen(true)}
            onBlur={() => setIsMainMenuOpen(isElementVisible(MAIN_MENU_ID))}
          >
            <Styled.MenuButton
              aria-controls={MAIN_MENU_ID}
              aria-haspopup
              aria-expanded={isMainMenuOpen}
              data-test={MAIN_MENU_BUTTON}
            >
              <MenuIcon size="medium" />
            </Styled.MenuButton>{' '}
          </Styled.Nav>
        }
      >
        <Styled.DropdownMenuLeft id={MAIN_MENU_ID} data-test={MAIN_MENU}>
          {mapItems(dropdownItems, DropdownItem)}
        </Styled.DropdownMenuLeft>
      </PopoverMenu>
      <Styled.LogoContainer>
        <AppLink route={routes.toCitationsHome()}>
          <Styled.LogoLink>
            <Styled.Logo
              alt={logoAlt}
              src={publicUrl('/static/logo.svg')}
              data-test="logo-button"
              onClick={handleSiteLogoClick}
              width={logoWidth}
              height={30}
            />
          </Styled.LogoLink>
        </AppLink>
      </Styled.LogoContainer>
      {navItems.header && (
        <Styled.HeaderNav role="navigation" aria-label="header" data-test="header-menu">
          <HeaderNav
            navItems={ebPremiumCitationsHeaderItems || navItems.header}
            style={style}
            projectId={projectId}
            onClick={handleHeaderNavClick}
          />
        </Styled.HeaderNav>
      )}
      <Styled.RightNav role="navigation" aria-label="account">
        {mapItems(navItems.right, NavLink)}
      </Styled.RightNav>
      {email && (
        <PopoverMenu
          position="bottomLeft"
          targetElement={
            <Styled.Nav
              role="navigation"
              onMouseOver={() => setIsAccountMenuOpen(true)}
              onMouseOut={() => setIsAccountMenuOpen(isElementVisible(ACCOUNT_MENU_ID))}
              onFocus={() => setIsAccountMenuOpen(true)}
              onBlur={() => setIsAccountMenuOpen(isElementVisible(ACCOUNT_MENU_ID))}
            >
              <Styled.AccountButtonWrapper>
                <Styled.UserDetails
                  aria-controls={ACCOUNT_MENU_ID}
                  aria-haspopup
                  aria-expanded={isAccountMenuOpen}
                  data-test={ACCOUNT_MENU_BUTTON}
                >
                  <HiddenLabel>account menu for</HiddenLabel>
                  {paidUser && (
                    <Styled.PlusBadge data-test="plusBadge">PLUS</Styled.PlusBadge>
                  )}
                  <ProfileIcon size="small" aria-hidden />
                  <Styled.Email>
                    <HiddenLabel>user</HiddenLabel> {email}
                  </Styled.Email>
                  <ChevronIcon size="xsmall" aria-hidden />
                </Styled.UserDetails>
              </Styled.AccountButtonWrapper>
            </Styled.Nav>
          }
        >
          <Styled.DropdownMenuRight id={ACCOUNT_MENU_ID}>
            {mapItems(navItems.account, DropdownItem)}
          </Styled.DropdownMenuRight>
        </PopoverMenu>
      )}
    </Styled.Header>
  )
}

const mapStateToProps = (state: AppState, props) => ({
  projectId: currentProjectIdSelector(state),
  style: currentStyleSelector(state),
  paidUser: isProSelector(state),
  email: userEmailSelector(state),
  navItems: props.navItemsSelector(state),
  isLoggedIn: loggedInSelector(state),
  authModalExpFlag: authModalExpFlagSelector(state),
  isGuestUser: isGuestSelector(state),
})

const mapDispatchToProps = {
  openModal,
  signupStart,
  signinStart,
  userClick,
}

export default connect(mapStateToProps, mapDispatchToProps)(Nav)
